import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import ReactDOMServer from 'react-dom/server';
import {
  AppDatePicker,
  AppFileUploader,
  AppInputNumber,
  AppModal,
  AppRemixIcon,
} from '@/layout/AppComponents';
import Translator from '../../../../../components/helpers/Translator';
import { axiosPath } from '../../../../../../routing/helpers/router';
import minions from '../../../../../../minions/minions';
import errorHandler from '../../../../../../layout/components/error/ErrorHandler';
import AppFileUploaderList from '../../../../../components/form/File/AppFileUploaderList';
import useIsMounted from '../../../../../components/hooks/useIsMounted';
import { handleDisabled, handleMinDate } from '../../tools/comsaReportsTools';

export default function ModuleComsaEnergyEfficiencyReportUpdate(props) {
  const { report, onRechargeReports, moduleData } = props;
  const { moduleBridgeHex, subModuleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const [showModalReport, setShowModalReport] = useState(false);
  const validationSchema = Yup.object().shape({
    year: Yup.date()
      .nullable()
      .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
    totalInvestmentSupermarket: Yup.number()
      .nullable()
      .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
    totalInvestmentEnergyEfficiencyMeasures: Yup.number()
      .nullable()
      .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
  });

  const handleUpdateReport = useCallback(
    async (payload) =>
      axios
        .post(
          axiosPath('api_r_user_module_comsaReports_editReport'),
          minions.forms.toFormData({
            ...payload,
            id: report.id,
            type: 'energyEfficiency',
            moduleBridgeHex,
            subModuleBridgeHex,
            plantHex,
          }),
          { headers: { 'Content-Type': 'multipart/form-data' } }
        )
        .then(() => {
          if (isMounted.current) onRechargeReports();
        })
        .catch((e) => dispatch(errorHandler(e)))
        .finally(() => setShowModalReport(false)),
    [moduleBridgeHex, subModuleBridgeHex, plantHex]
  );

  return (
    <>
      <button
        type="button"
        className="btn btn-info btn-xs mr-1"
        onClick={() => setShowModalReport(true)}
        title={ReactDOMServer.renderToString(
          <Translator file="module-comsaReports" name="edit" />
        )}
        disabled={handleDisabled(moduleData, report?.year)}
      >
        <AppRemixIcon icon="edit" />
      </button>
      <AppModal
        show={showModalReport}
        title="Editar Eficiencia Energética"
        size="lg"
        onClose={() => setShowModalReport(false)}
      >
        <Formik
          initialValues={{
            year: report.year,
            totalInvestmentSupermarket: report.totalSupermarket,
            totalInvestmentEnergyEfficiencyMeasures: report.totalEfficiency,
            files: [],
            deletedFiles: [],
          }}
          validationSchema={validationSchema}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={handleUpdateReport}
        >
          {({ setFieldValue, handleSubmit, values, errors }) => (
            <>
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <AppInputNumber
                      icon={<AppRemixIcon icon="store-2" />}
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaReports"
                          name="total-investment-supermarket"
                        />
                      )}
                      value={values.totalInvestmentSupermarket}
                      placeholder={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaReports"
                          name="value-euros"
                        />
                      )}
                      onChange={(v) =>
                        setFieldValue('totalInvestmentSupermarket', v)
                      }
                      isClearable
                      error={errors.totalInvestmentSupermarket}
                    />
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="mb-3">
                    <AppInputNumber
                      icon={<AppRemixIcon icon="lightbulb-flash" />}
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaReports"
                          name="total-investment-energy-efficiency-measures"
                        />
                      )}
                      value={values.totalInvestmentEnergyEfficiencyMeasures}
                      placeholder={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaReports"
                          name="value-euros"
                        />
                      )}
                      onChange={(v) =>
                        setFieldValue(
                          'totalInvestmentEnergyEfficiencyMeasures',
                          v
                        )
                      }
                      isClearable
                      error={errors.totalInvestmentEnergyEfficiencyMeasures}
                    />
                  </div>
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-md-6">
                  <div className="mb-3">
                    <AppDatePicker
                      title={
                        <Translator file="module-comsaReports" name="year" />
                      }
                      value={values.year}
                      onChange={(opt) => setFieldValue('year', opt)}
                      minDate={handleMinDate(moduleData)}
                      dateFormat="yyyy"
                      displayDateFormat="YYYY"
                      placeholder={
                        <Translator
                          file="module-comsaReports"
                          name="select.a.year"
                        />
                      }
                      isClearable
                      showYearPicker
                      popperClassName="react-datepicker-popper react-datepicker-popper-1000"
                      error={errors.year}
                    />
                  </div>
                </div>
              </div>

              <div>
                <div className="text-primary mt-3">
                  <Translator
                    file="module-comsaReports"
                    name="attach.evidence"
                  />
                </div>

                <div className="row mb-3">
                  <div className="col-sm-12 col-lg-6">
                    <AppFileUploader
                      isMulti
                      placeholder={
                        !values.files || Object.keys(values?.files).length === 0
                          ? ReactDOMServer.renderToString(
                              <Translator
                                file="module-legalCompliance"
                                name="select.one.multiple.files"
                              />
                            )
                          : ReactDOMServer.renderToString(
                              <Translator
                                file="general"
                                name="select.number.of.elements"
                                variables={{
                                  elements: Object.keys(values?.files).length,
                                }}
                              />
                            )
                      }
                      onChange={(opt) => setFieldValue('files', opt)}
                      btnText={<Translator file="general" name="search..." />}
                    />
                  </div>

                  <div className="col-sm-12 col-lg-6">
                    <AppFileUploaderList
                      values={values}
                      setFieldValue={setFieldValue}
                      urlsAvaliable={report.files}
                    />
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-12">
                  <button
                    className="btn btn-success btn-block"
                    type="submit"
                    onClick={handleSubmit}
                  >
                    <Translator file="module-comsaReports" name="edit" />
                  </button>
                </div>
              </div>
            </>
          )}
        </Formik>
      </AppModal>
    </>
  );
}
